/**
 * The single source of truth for the iframe security attributes.
 *
 * `allow` is resolved per role. The call frame owns the media, the transport and the auth session;
 * the pip frame is a view/controls terminal fed over a MessagePort, so one shared set would hand it
 * capabilities it has no way to use.
 */
import type { FrameRole } from './protocol';

/**
 * `sandbox` restricts the framed document to the minimum it needs. Identical for both frames:
 *   allow-scripts        — run the SDK bundle
 *   allow-same-origin    — keep the frame on the Quarterzip origin, which auth-session
 *                          persistence, storage and WebRTC all require. Also required for the
 *                          frame to post messages under its real origin: without it the document
 *                          gets an opaque origin, which arrives as `"null"` and fails the origin
 *                          checks on both sides. Safe ONLY while the frame is cross-origin to the
 *                          host page — same-origin framing lets the frame remove its own sandbox.
 *   allow-popups         — `target="_blank"` links
 *   allow-modals         — alert/confirm surfaces
 *   allow-forms          — in-frame form submission
 */
export const SANDBOX = 'allow-scripts allow-same-origin allow-popups allow-modals allow-forms';

/**
 * `allow` (Permissions Policy) delegates features to the frame's origin. Delegation only works if
 * the TOP-LEVEL integrator page is itself permitted to use each feature; see {@link https://docs.quarterzip.ai}.
 *
 * `call` — the frame that captures and authenticates:
 *   microphone          — the call audio
 *   display-capture     — screen share (getDisplayMedia)
 *   autoplay            — agent audio that starts without a user gesture: a reconnect, or an agent
 *                         that greets on load. The in-frame "Join" click covers the ordinary path.
 *   speaker-selection   — output device picker, included only when the browser supports selectAudioOutput.
 *   storage-access      — lets the frame call the Storage Access API on browsers that block,
 *                         rather than partition, third-party storage, so the auth session persists
 */

// speaker-selection is only recognised by browsers that implement selectAudioOutput(). Including it
// elsewhere (Chrome today) logs "Unrecognized feature".
const supportsSpeakerSelection = (): boolean =>
	typeof MediaDevices !== 'undefined' && 'selectAudioOutput' in MediaDevices.prototype;

// `speaker-selection` is added to both sets only if browser supports permission
const ALLOW: Record<FrameRole, string> = {
	call: 'microphone; display-capture; autoplay; storage-access;',
	pip: ''
};

const buildAllow = (role: FrameRole): string => {
	let permissions = ALLOW[role];
	if (supportsSpeakerSelection()) permissions += ' speaker-selection;';
	return permissions;
};

/** Apply the standard security attributes and load URL to a freshly created iframe element. */
export const configureFrame = (iframe: HTMLIFrameElement, src: string, role: FrameRole): void => {
	iframe.setAttribute('sandbox', SANDBOX);
	iframe.setAttribute('allow', buildAllow(role));
	iframe.setAttribute('title', 'Quarterzip');
	iframe.setAttribute('referrerpolicy', 'no-referrer');
	iframe.src = src;
};
